import React, { CSSProperties, useCallback, useState } from "react";

import { useNotifications } from "@/contexts/NotificationsContext";
import { useApi } from "@/hooks/useApi";

/** Teal, matching the Hi-Res download pill (see config.tsx ICONIK_HIRES_COLOR). */
const HIRES_COLOR = "#4ecdc4";

/**
 * Pill styling that mirrors the Hi-Res/Proxy download pills. Kept local
 * (rather than imported from config.tsx) to avoid an import cycle, since
 * config.tsx renders this component from its download-column cell.
 */
const basePillStyle: CSSProperties = {
  padding: "3px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  lineHeight: "normal",
  border: "1px solid",
  background: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
  color: HIRES_COLOR,
  borderColor: HIRES_COLOR,
};

const disabledPillStyle: CSSProperties = {
  ...basePillStyle,
  cursor: "default",
  opacity: 0.5,
};

/** Response shape from POST /subclips/{assetId}/hi-res. */
interface HiResResponse {
  status?: "started" | "in_progress" | "already_available";
  assetId?: string;
}

export interface CreateHiResButtonProps {
  /** The sub-clip asset id to render a hi-res file for. */
  assetId: string;
}

/**
 * "Create Hi-Res" pill shown in the download column for a sub-clip that has no
 * hi-res file yet. Clicking it triggers the Iconik custom action that renders
 * one (a 2-4 minute async transcode). Because completion is surfaced only on a
 * fresh page load (the render attaches an S3 file that the next GET /assets
 * turns into a Hi-Res download pill), the button just fires the request, toasts
 * guidance to refresh, and disables itself for the rest of this view so the
 * user doesn't queue duplicate renders. A page refresh re-derives state from
 * the server: a finished render shows the Hi-Res pill; an unfinished/failed one
 * shows this button again (the backend refuses a duplicate while one is still
 * in flight).
 */
const CreateHiResButton: React.FC<CreateHiResButtonProps> = ({ assetId }) => {
  const { apiPost } = useApi();
  const { notifySuccess, notifyError } = useNotifications();
  const [state, setState] = useState<"idle" | "submitting" | "requested">("idle");

  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      // Never let the click bubble to the row (which opens the Clip Preview).
      event.stopPropagation();
      if (state !== "idle") return;
      setState("submitting");
      try {
        const res = await apiPost<HiResResponse>(
          `/subclips/${encodeURIComponent(assetId)}/hi-res`
        );
        switch (res?.status) {
          case "started":
            notifySuccess(
              "Hi-res generation started for this clip. It usually takes 2-4 minutes — " +
                "refresh the page to see the Hi-Res download button appear."
            );
            setState("requested");
            break;
          case "in_progress":
            notifySuccess(
              "Hi-res generation is already in progress for this clip. " +
                "Refresh the page in a couple of minutes to download it."
            );
            setState("requested");
            break;
          case "already_available":
            notifySuccess(
              "A Hi-Res version of this clip already exists. " +
                "Refresh the page to see the Hi-Res download button."
            );
            setState("requested");
            break;
          default:
            notifyError(
              "Hi-Res",
              "Couldn't start hi-res generation for this clip. Please try again."
            );
            setState("idle");
        }
      } catch {
        notifyError(
          "Hi-Res",
          "Couldn't start hi-res generation for this clip. Please try again."
        );
        setState("idle");
      }
    },
    [apiPost, assetId, notifyError, notifySuccess, state]
  );

  const label =
    state === "submitting"
      ? "Starting…"
      : state === "requested"
      ? "Requested"
      : "Create Hi-Res";

  return (
    <button
      type="button"
      data-testid="create-hires-button"
      onClick={handleClick}
      disabled={state !== "idle"}
      title={
        state === "requested"
          ? "Hi-res generation requested — refresh the page in a couple of minutes"
          : "Create a downloadable hi-res version of this clip"
      }
      style={state === "idle" ? basePillStyle : disabledPillStyle}
    >
      {label}
    </button>
  );
};

export default CreateHiResButton;
